import React from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { handleClickedTabChange } from "./SiteAboutPageUtils/handleClickedTabChange";
import {
  AlertState,
  SiteInfo,
  PatchObject,
  WireGuardTunnel,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { WireGuardEditSession } from "./WireGuardTabEditMode";
import { shouldShowWebhooksTab } from "./SiteAboutPageUtils/siteWebhooks";
import { DOUBLE_TAKE_TAB } from "./SiteAboutPageUtils/siteDoubleTake";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const TabsComponet = ({
  clickedTab,
  setClickedTab,
  editMode,
  setActiveTab,
  setPatchObject,
  setSiteInfo,
  vmsVariables,
  setVmsVariables,
  siteSchedules,
  siteInfo,
  setSiteSchedules,
  setSchedulesTabActive,
  setBackdropLoadingScreenOn,
  setAlertState,
  setSyncNeeded,
  setSiteInfoChangesMade,
  setScheduleChangesMade,
  setDeleteScheduleDialogOpen,
  recordingServerData,
  recordingServerPatchObject,
  setRecordingServerPatchObject,
  setRecordingServerChangesMade,
  recordingServerVisible,
  healthCheckData,
  setCameraHealthPatchObject,
  setCameraHealthChangesMade,
  setAddCameraHealthDialogOpen,
  setTurnOffHealthDialogOpen,
  webhooksInfo,
  setWebhooksPatchObject,
  setWebhooksChangesMade,
  setEditMode,
  showWireguardTab,
  wireguardTunnels,
  wireguardTunnelsLoading,
  selectedWireguardTunnelId,
  setSelectedWireguardTunnelId,
  onWireGuardSessionUpdate,
  setWireguardChangesMade,
  showDoubleTakeTab = false,
}: {
  clickedTab: string;
  setClickedTab: Dispatcher<string>;
  editMode: boolean;
  setActiveTab: Dispatcher<JSX.Element>;
  setPatchObject: Dispatcher<PatchObject>;
  setSiteInfo: Dispatcher<SiteInfo>;
  vmsVariables: (string | string[] | undefined)[][];
  setVmsVariables: Dispatcher<(string | string[] | undefined)[][]>;
  siteSchedules: any;
  siteInfo: SiteInfo;
  setSiteSchedules: Dispatcher<any[]>;
  setSchedulesTabActive: Dispatcher<boolean>;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setAlertState: Dispatcher<AlertState>;
  setSyncNeeded: Dispatcher<boolean>;
  setSiteInfoChangesMade: Dispatcher<boolean>;
  setScheduleChangesMade: Dispatcher<boolean>;
  setDeleteScheduleDialogOpen: Dispatcher<boolean>;
  recordingServerData: any;
  recordingServerPatchObject: any;
  setRecordingServerPatchObject: any;
  setRecordingServerChangesMade: any;
  recordingServerVisible: boolean;
  healthCheckData: any;
  setCameraHealthPatchObject: any;
  setCameraHealthChangesMade: Dispatcher<boolean>;
  setAddCameraHealthDialogOpen: Dispatcher<boolean>;
  setTurnOffHealthDialogOpen: Dispatcher<boolean>;
  webhooksInfo: any;
  setWebhooksPatchObject: any;
  setWebhooksChangesMade: any;
  setEditMode: any;
  showWireguardTab: boolean;
  wireguardTunnels: WireGuardTunnel[];
  wireguardTunnelsLoading: boolean;
  selectedWireguardTunnelId: number | null;
  setSelectedWireguardTunnelId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  onWireGuardSessionUpdate: (session: WireGuardEditSession) => void;
  setWireguardChangesMade: Dispatcher<boolean>;
  showDoubleTakeTab?: boolean;
}) => {
  const { evalinkSite } = useSiteAboutStore();
  return (
    <Box sx={{ width: "100%" }}>
      <ThemeProvider theme={theme}>
        <Tabs
          value={clickedTab}
          onChange={(event, newValue) =>
            handleClickedTabChange(
              event,
              newValue,
              setClickedTab,
              editMode,
              setActiveTab,
              setPatchObject,
              setSiteInfo,
              vmsVariables,
              setVmsVariables,
              siteSchedules,
              siteInfo,
              setSiteSchedules,
              setSchedulesTabActive,
              setBackdropLoadingScreenOn,
              setAlertState,
              setSyncNeeded,
              setSiteInfoChangesMade,
              setScheduleChangesMade,
              setDeleteScheduleDialogOpen,
              recordingServerData,
              recordingServerPatchObject,
              setRecordingServerPatchObject,
              setRecordingServerChangesMade,
              healthCheckData,
              setCameraHealthPatchObject,
              setCameraHealthChangesMade,
              setAddCameraHealthDialogOpen,
              setTurnOffHealthDialogOpen,
              webhooksInfo,
              setWebhooksPatchObject,
              setWebhooksChangesMade,
              setEditMode,
              showWireguardTab,
              wireguardTunnels,
              wireguardTunnelsLoading,
              selectedWireguardTunnelId,
              setSelectedWireguardTunnelId,
              onWireGuardSessionUpdate,
              setWireguardChangesMade
            )
          }
          aria-label="tabs"
          color="primary"
        >
          {/* For autopatrol sites, show Schedule Info tab first */}
          {siteInfo.integration_type_name === "autopatrol" && (
            <Tab
              value="three"
              label="Schedule Info"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
          <Tab
            value="one"
            label={
              siteInfo.integration_type_name === "autopatrol"
                ? "General Site Info"
                : "General Info"
            }
            sx={{ fontFamily: "Mulish", fontWeight: "Bold", color: "primary" }}
          />
          {/* For non-autopatrol sites, show Schedules tab after General Info */}
          {Boolean(siteInfo.display_schedule) === true &&
            siteInfo.integration_type_name !== "autopatrol" && (
              <Tab
                value="three"
                label="Schedules"
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "Bold",
                  color: "primary",
                }}
              />
            )}
          {localStorage.SMTPSite === "true" &&
            siteInfo.integration_type_name === "SMTP_per_camera" && (
              <Tab
                value="four"
                label="SMTP Settings"
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "Bold",
                  color: "primary",
                }}
              />
            )}
          {recordingServerVisible && (
            <Tab
              value="five"
              label="Recording Servers"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
          {siteInfo.chm && siteInfo.integration_type_name !== "autopatrol" && (
            <Tab
              value="six"
              label="Camera Health"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
          {shouldShowWebhooksTab(siteInfo, webhooksInfo) && (
            <Tab
              value="seven"
              label="Webhooks"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
          {evalinkSite && (
            <Tab
              value="eight"
              label="Evalink"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
          {showWireguardTab && (
            <Tab
              value="nine"
              label="WireGuard"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
          {showDoubleTakeTab && (
            <Tab
              value={DOUBLE_TAKE_TAB}
              label="DoubleTake"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "primary",
              }}
            />
          )}
        </Tabs>
      </ThemeProvider>
    </Box>
  );
};
